import React, { useState } from "react"

// ConnectorDialog is imported directly rather than through the features/connectors
// barrel: that barrel also re-exports ConnectorsTable, which imports this forms
// directory back, and the resulting cycle drags the whole table stack in behind a
// single dialog. TargetForm resolves it the same way.
import ConnectorDialog from "../../ConnectorDialog"
import { ConfigFormField } from "../AdditionalConfigForm/components"
import type { AdditionalConfigField } from "../AdditionalConfigForm/types"

import { connectorIcons } from "~/ui-rtk/shared/constants/image-paths.contants"
import { Button } from "~/ui-rtk/components/ui/controls"

type AmazonSellingPartnerFormProps = {
  isOpen: boolean
  onClose: () => void
  onSubmit: (preConfigValues: Record<string, unknown>) => void
}

// Rendered with ConfigFormField — the same field component every Airbyte
// pre-config dialog (AdditionalConfigForm) uses — so this popup matches their
// look exactly: same label treatment, input, and info tooltip.
const MERCHANT_TOKEN_FIELD: AdditionalConfigField = {
  format: "string",
  property_label: "Merchant Token",
  configuration_path: "merchant_id",
  info: "Amazon shows this in Seller Central under Account Info. Copy the token while viewing the marketplace you want synced.",
}

const noop = () => undefined

/**
 * Amazon Selling Partner needs its Merchant Token *before* the connector exists:
 * Fivetran validates `config` on create and rejects the call with "Merchant ID is
 * required for Seller accounts" when it's missing (MD-5391). So unlike the
 * serviceConnectorMap forms — which open after connect() — this one runs first and
 * hands the value back to be sent as preConfig. Styled to match the Airbyte
 * pre-config dialog (AdditionalConfigForm): same ConnectorDialog wrapper, intro
 * copy, ConfigFormField field, and full-width "Complete Setup" button that stays
 * disabled until the field is filled.
 */
const AmazonSellingPartnerForm: React.FC<AmazonSellingPartnerFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState<Record<string, unknown>>({})

  const merchantToken =
    typeof formData.merchant_id === "string" ? formData.merchant_id.trim() : ""
  const isFormValid = merchantToken.length > 0

  return (
    <ConnectorDialog
      imgSrc={connectorIcons.amazonSellingPartner}
      alt="Amazon Selling Partner Logo"
      title="Connection to Amazon Selling Partner"
      isOpen={isOpen}
      onClose={onClose}
    >
      <p className="leading-5 text-left text-base text-platinum">
        Complete the setup for Amazon Selling Partner by providing your Merchant
        Token — find it in Seller Central under Account Info &rarr; Your
        Merchant Token, for the marketplace you want synced. Double-check the
        value before continuing: the setup card shows it read-only afterwards.
      </p>

      <div className="flex flex-col w-full gap-4 mt-8">
        <div className="space-y-6">
          <ConfigFormField
            config={MERCHANT_TOKEN_FIELD}
            formData={formData}
            selectedPrefixes={{}}
            tagInputValues={{}}
            onInputChange={(fieldName, value) =>
              setFormData(prev => ({ ...prev, [fieldName]: value }))
            }
            onPrefixChange={noop}
            onTagInputChange={noop}
            onRemoveTag={noop}
            onAddTag={noop}
            getTags={() => []}
          />
        </div>

        <Button
          variant={{ variant: "solid", color: "white" }}
          onClick={() => onSubmit({ merchant_id: merchantToken })}
          disabled={!isFormValid}
          className="w-full"
        >
          Complete Setup
        </Button>
      </div>
    </ConnectorDialog>
  )
}

export default AmazonSellingPartnerForm
